import { SettingsSection } from '@/modules/settings/components/SettingsSection'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '@/ui'
import { faArrowRotateLeftSolid } from '@/lib/fa'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { CountrySelection } from './CountrySelection'
import { SettingsPageWrapper } from '@/modules/settings/components/SettingsPageWrapper'
import { RESTORABLE_COUNTRIES } from '../config'
import { useMinimumWageRestoreMutation } from '../useMinimumWageRestoreMutation'

type Props = {
  selectedCountryValue: string
  isDirty: boolean
  handleSave: () => void
  handleCancel: () => void
  children: React.ReactNode
  dataTestId?: string
  isLoading?: boolean
  diffBefore?: Record<string, unknown>
  diffAfter?: Record<string, unknown>
  diffLabels?: Record<string, string>
  diffRenderers?: Record<string, (before: unknown, after: unknown) => { before: React.ReactNode; after: React.ReactNode }>
}

export const MinimumWageCountryLayout = ({
  selectedCountryValue,
  isDirty,
  handleCancel,
  handleSave,
  children,
  dataTestId,
  isLoading,
  diffBefore,
  diffAfter,
  diffLabels,
  diffRenderers,
}: Props) => {
  const { t } = useLocalize()
  const { companyId } = useParams<{ companyId: string }>()
  const wrapConfirm = useWrapConfirm()
  const restore = useMinimumWageRestoreMutation(companyId)

  // Ekran ogólny przywraca wszystkie kraje naraz.
  const isGeneral = !selectedCountryValue
  const canRestore = isGeneral || RESTORABLE_COUNTRIES.has(selectedCountryValue)

  const countryLabel = isGeneral
    ? ''
    : t(`configuration.minimumWages.${selectedCountryValue}`).toString()

  const restoreAction = canRestore ? (
    <Button
      variant="secondary"
      size="sm"
      className="tw-rounded-lg tw-px-4"
      faLeadingIcon={faArrowRotateLeftSolid}
      disabled={isDirty}
      loading={restore.isPending}
      data-test-id="minimum-wage-restore-defaults"
      text={t(
        isGeneral
          ? 'configuration.minimumWages.restoreDefaultsAll'
          : 'configuration.minimumWages.restoreDefaults',
      ).toString()}
      onClick={wrapConfirm(
        () => restore.mutate(isGeneral ? undefined : selectedCountryValue),
        {
          message: isGeneral
            ? t('configuration.minimumWages.restoreConfirmAll').toString()
            : t('configuration.minimumWages.restoreConfirm')
                .toString()
                .replace('{0}', countryLabel),
        },
      )}
    />
  ) : null

  return (
    <SettingsPageWrapper
      isReady={true}
      isPending={isLoading || restore.isPending}
      globalErrorMsg="There was an error loading the Minimum Wages Configuration Page."
      isDirty={isDirty}
      handleSave={handleSave}
      handleReset={handleCancel}
      diffBefore={diffBefore}
      diffAfter={diffAfter}
      diffLabels={diffLabels}
      diffRenderers={diffRenderers}
    >
      <SettingsSection
        dataTestId={dataTestId}
        title={t('configuration-menu.minimum-wages').toString()}
        leadingIcon={
          <img
            src={`/assets/icons_nucleo/icons/configuration/Placa_minimalna_gray.svg`}
          />
        }
        actions={restoreAction}
      >
        <CountrySelection 
          selectedCountryValue={selectedCountryValue}
          // isDirty={isDirty} 
        />

        <div className='tw-flex tw-flex-col tw-gap-4 tw-p-2'>{children}</div>
      </SettingsSection>
    </SettingsPageWrapper>
  )
}
